import { t as tr } from './i18n';
import { fmtNum } from './format';
import type { ItemSpecs } from './types';

/**
 * Caracteristicile tehnice ale unei poziții (înălțimea, grosimea), separat de
 * descriere. Regula de la cofraje: **cantitățile pe înălțimi diferite nu se
 * adună**. „500 mp la 3 m + 300 mp la 5 m” sunt două poziții. „Cofraje pentru
 * 3, 4 și 6 m” fără cantități separate rămâne o poziție: la planșeu se
 * folosesc aceleași materiale, diferă doar popii; înălțimea după care se
 * ofertează e cea mai mare, iar toate rămân scrise, pentru mai târziu.
 */

/** Pozițiile la care o înălțime scrisă în descriere chiar e o înălțime. */
const HEIGHT_CONTEXT = /cofra|plan[sș]e|pop[ie]|sprijin|schel|e[sș]afod|turnare/i;

/** Un număr cu virgulă sau punct: „3”, „3,5”, „4.20”. */
const NUM = String.raw`\d+(?:[.,]\d+)?`;

const toNum = (s: string): number => Number(s.replace(',', '.'));

/**
 * Înălțimile scrise în descriere, în m, crescător și fără repetiții. Citește
 * doar forme fără echivoc: „H=3 m”, „înălțime 3,5 m”, „la 4 m”, „3, 4 și 6 m”.
 * „100 m de țeavă” nu e înălțime: fără context de cofraj nu se citește nimic.
 */
export function heightsInText(desc: string): number[] {
  if (!HEIGHT_CONTEXT.test(desc)) return [];
  const found: number[] = [];
  // listă: „3, 4 și 6 m”, „3/4/6 m”, „3 - 6 m” nu (e un interval, nu o listă)
  const list = new RegExp(
    String.raw`(?:^|[^\d.,])((?:${NUM}\s*(?:,|/|;|și|si|sau)\s*)+${NUM})\s*m(?![a-zăâîșț0-9²])`,
    'gi',
  );
  for (const m of desc.matchAll(list)) {
    for (const n of m[1]!.split(/\s*(?:,(?!\d)|\/|;|și|si|sau)\s*/i)) {
      if (n.trim()) found.push(toNum(n.trim()));
    }
  }
  // o singură înălțime, cu context: „H=3 m”, „h 3,5m”, „înălțime 4 m”, „la 4 m”
  const one = new RegExp(
    String.raw`(?:\bh\s*=?\s*|[îi]n[aă]l[tț]ime(?:\s+de)?\s*(?:turnare\s*)?:?\s*|\bla\s+)(${NUM})\s*m(?![a-zăâîșț0-9²])`,
    'gi',
  );
  for (const m of desc.matchAll(one)) found.push(toNum(m[1]!));
  return [...new Set(found.filter((n) => Number.isFinite(n) && n > 0 && n <= 60))].sort((a, b) => a - b);
}

/**
 * Caracteristicile curățate: înălțimile crescător, înălțimea principală = cea
 * mai mare. null dacă nu e nimic.
 */
export function normalizeSpecs(s: ItemSpecs | null | undefined): ItemSpecs | null {
  if (!s) return null;
  const pos = (n: number | null | undefined) => (n != null && Number.isFinite(n) && n > 0 ? n : null);
  const heights = [...new Set((s.heights ?? []).map(pos).filter((n): n is number => n != null))].sort(
    (a, b) => a - b,
  );
  const height = pos(s.height) ?? (heights.length ? heights[heights.length - 1]! : null);
  const thickness = pos(s.thickness);
  if (height == null && thickness == null) return null;
  const out: ItemSpecs = {};
  if (height != null) out.height = height;
  if (heights.length > 1) out.heights = heights;
  if (thickness != null) out.thickness = thickness;
  return out;
}

/**
 * Caracteristicile unei poziții: cele date explicit sau, lipsind, înălțimile
 * citite din descriere. Așa „cofraje pentru 3, 4 și 6 m” scris de mână ajunge
 * tot la o poziție cu înălțimea 6 m și lista 3 / 4 / 6.
 */
export function specsFor(desc: string, given: ItemSpecs | null | undefined): ItemSpecs | null {
  const s = normalizeSpecs(given);
  if (s) return s;
  const hs = heightsInText(desc);
  return hs.length ? normalizeSpecs({ heights: hs }) : null;
}

/** „H 6 m (3 / 4 / 6 m)”, „H 3 m · 20 cm”; '' fără nimic. */
export function specsText(s: ItemSpecs | null | undefined): string {
  if (!s) return '';
  const parts: string[] = [];
  if (s.height != null) {
    parts.push(
      s.heights && s.heights.length > 1
        ? tr('H {0} m ({1} m)', fmtNum(s.height), s.heights.map(fmtNum).join(' / '))
        : tr('H {0} m', fmtNum(s.height)),
    );
  }
  if (s.thickness != null) parts.push(`${fmtNum(s.thickness)} cm`);
  return parts.join(' · ');
}

/**
 * Pozițiile cu aceeași descriere și unitate, dar înălțimi diferite, au nevoie de
 * înălțime în tabel: altfel două rânduri „Cofraj planșeu” arată la fel.
 */
export function needsSpecsColumn(items: { specs?: ItemSpecs | null }[]): boolean {
  return items.some((it) => specsText(it.specs) !== '');
}
